import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage, translations } from '../context/LanguageContext';

const { FiSun, FiClock, FiMoon, FiCheck, FiChevronDown, FiChevronUp } = FiIcons;

const RoutineSection = ({ routines, onToggleRoutineItem, onResetRoutines }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [expandedSections, setExpandedSections] = useState({
    morning: true,
    day: true,
    evening: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const routineSections = [
    {
      key: 'morning',
      title: t.morningRoutine,
      icon: FiSun,
      color: 'text-yellow-300',
      bgColor: 'from-yellow-500/20 to-orange-500/20',
      borderColor: 'border-yellow-500/30',
      items: [
        { key: 'affirmation', text: t.affirmation },
        { key: 'meditation', text: t.meditation }
      ]
    },
    {
      key: 'day',
      title: t.dayRoutine,
      icon: FiClock,
      color: 'text-blue-300',
      bgColor: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      items: [
        { key: 'dayPlan', text: t.dayPlan }
      ]
    },
    {
      key: 'evening',
      title: t.eveningRoutine,
      icon: FiMoon,
      color: 'text-purple-300',
      bgColor: 'from-purple-500/20 to-indigo-500/20',
      borderColor: 'border-purple-500/30',
      items: [
        { key: 'dayReflection', text: t.dayReflection },
        { key: 'dailyLearning', text: t.dailyLearning },
        { key: 'weekPlanCheck', text: t.weekPlanCheck },
        { key: 'tomorrowPlan', text: t.tomorrowPlan }
      ]
    }
  ];

  const getCompletionRate = (sectionKey) => {
    const section = routines[sectionKey];
    if (!section) return 0;
    const completed = Object.values(section).filter(Boolean).length;
    const total = Object.keys(section).length;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={FiClock} className="text-2xl text-green-300 mr-3" />
          <h2 className="text-2xl font-bold text-white">{t.dailyRoutines}</h2>
        </div>
        <button
          onClick={onResetRoutines}
          className="px-3 py-1 bg-white/10 text-white/70 rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
        >
          {t.resetDaily}
        </button>
      </div>

      <div className="space-y-4">
        {routineSections.map((section) => {
          const completionRate = getCompletionRate(section.key);
          const isExpanded = expandedSections[section.key];
          
          return (
            <motion.div
              key={section.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-gradient-to-br ${section.bgColor} backdrop-blur-md rounded-xl border ${section.borderColor}`}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleSection(section.key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={section.icon} className={`text-xl ${section.color} mr-3`} />
                    <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-white/80">
                      {Math.round(completionRate)}%
                    </div>
                    <SafeIcon 
                      icon={isExpanded ? FiChevronUp : FiChevronDown} 
                      className="text-white/60" 
                    />
                  </div>
                </div>
                
                <div className="mt-3">
                  <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className="bg-gradient-to-r from-white/60 to-white/80 h-full transition-all duration-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 pb-4"
                  >
                    <div className="space-y-2">
                      {section.items.map((item) => (
                        <motion.div
                          key={item.key}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all duration-200"
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleRoutineItem(section.key, item.key);
                            }}
                            className={`mr-3 p-2 rounded-full transition-all duration-200 ${
                              routines[section.key]?.[item.key]
                                ? 'bg-green-500 text-white'
                                : 'bg-white/10 text-white/60 hover:bg-green-500/20'
                            }`}
                          >
                            <SafeIcon icon={FiCheck} className="text-sm" />
                          </button>
                          <span
                            className={`flex-1 ${
                              routines[section.key]?.[item.key]
                                ? 'text-white/80 line-through'
                                : 'text-white'
                            }`}
                          >
                            {item.text}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-lg border border-green-500/30">
        <p className="text-green-200 text-sm text-center italic">
          "{t.routineQuote}"
        </p>
      </div>
    </motion.div>
  );
};

export default RoutineSection;